/**
 * SectionsManager — Admin UI for the section-based CMS (Phase B Step 5).
 *
 * Features
 *   • List all sections grouped by page, ordered by `order`
 *   • Filter by page_slug and status (draft/published)
 *   • Inline JSON editor for `content` (pragmatic v1 — a per-field-type editor
 *     lands in a later iteration once a section-type registry exists)
 *   • Media picker button — searches `/api/cms/media/search` and inserts the
 *     selected `asset_key` at the cursor position
 *   • Drag-and-drop reorder (HTML5 native DnD) with server-side PUT of `order`
 *   • Publish / Revert / Delete / Version-history buttons
 *   • Split-pane preview — right column iframes the target page with
 *     `?preview=<token>` so admins see draft content live
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  GripVertical, Search, Image as ImageIcon, Eye, Save, RotateCcw, Trash2,
  History, CheckCircle2, ExternalLink, Plus, RefreshCw, Code2, LayoutList,
} from 'lucide-react';
import StructuredEditor from './cms/StructuredEditor';
import { getSectionSchema } from './cms/sectionSchemas';
import SectionDiffStrip from './cms/SectionDiffStrip';

const API_URL = window.location.origin;

const PAGE_TO_ROUTE = {
  homepage: '/',
  header: '/',
  footer: '/',
  about: '/about',
  contact: '/contact',
  services: '/services',
  programs: '/programs',
  destinations: '/destinations',
  universities: '/universities',
  'success-stories': '/success-stories',
  locations: '/locations',
  ib: '/ib',
};

const KNOWN_PAGES = Object.keys(PAGE_TO_ROUTE);

const SectionsManager = () => {
  const { token } = useCMSAuth();
  const [pageSlug, setPageSlug] = useState('homepage');
  const [statusFilter, setStatusFilter] = useState('');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [draftJson, setDraftJson] = useState('');
  const [draftContent, setDraftContent] = useState(null);   // structured-editor state (parsed object)
  const [editorMode, setEditorMode] = useState('structured'); // 'structured' | 'raw'
  const [jsonError, setJsonError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [mediaQuery, setMediaQuery] = useState('');
  const [mediaResults, setMediaResults] = useState([]);
  // When set, clicking a media result invokes this callback with the asset_key
  // instead of inserting into the raw JSON textarea. Used by StructuredEditor.
  const [mediaPickCallback, setMediaPickCallback] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [newSectionOpen, setNewSectionOpen] = useState(false);
  const [newSlug, setNewSlug] = useState('homepage');
  const [newSecId, setNewSecId] = useState('');
  const textareaRef = useRef(null);
  const initialAutoSelect = useRef(true);   // only auto-select the first section on the very first successful load

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // -------- Fetch list --------
  const loadSections = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (pageSlug) params.set('page_slug', pageSlug);
    if (statusFilter) params.set('status', statusFilter);
    try {
      const res = await fetch(`${API_URL}/api/cms/sections/list?${params}`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setSections(data);
        if (initialAutoSelect.current && data.length) {
          initialAutoSelect.current = false;
          const first = data[0];
          setSelectedId(first.id);
          setDraftJson(JSON.stringify(first.content || {}, null, 2));
          setDraftContent(first.content || {});
          const schema = getSectionSchema(first.page_slug, first.section_id);
          setEditorMode(schema ? 'structured' : 'raw');
        }
      }
    } catch (e) {
      toast.error('Failed to load sections');
    } finally {
      setLoading(false);
    }
  }, [pageSlug, statusFilter, token]);

  // Reset the initial auto-select when the page filter changes so the first
  // section of the newly-selected page is picked.
  useEffect(() => {
    initialAutoSelect.current = true;
    setSelectedId(null);
    setDraftJson('');
    setDraftContent(null);
  }, [pageSlug]);

  useEffect(() => { loadSections(); }, [loadSections]);

  const selected = sections.find((s) => s.id === selectedId) || null;

  const selectSection = (sec) => {
    setSelectedId(sec.id);
    setDraftJson(JSON.stringify(sec.content || {}, null, 2));
    setDraftContent(sec.content || {});
    setJsonError(null);
    const schema = getSectionSchema(sec.page_slug, sec.section_id);
    setEditorMode(schema ? 'structured' : 'raw');
  };

  // -------- Save draft --------
  const handleSave = async () => {
    if (!selected) return;
    let parsed;
    try {
      if (editorMode === 'structured') {
        parsed = draftContent ?? {};
      } else {
        parsed = JSON.parse(draftJson);
      }
      setJsonError(null);
    } catch (e) {
      setJsonError(e.message);
      toast.error(`Invalid JSON: ${e.message}`);
      return;
    }
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/cms/sections/${selected.id}`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify({ content: parsed }),
      });
      if (res.ok) {
        toast.success('Draft saved');
        await loadSections();
      } else {
        toast.error('Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!selected) return;
    if (!window.confirm('Publish this section? The live site will update immediately.')) return;
    const res = await fetch(`${API_URL}/api/cms/sections/${selected.id}/publish`, {
      method: 'POST', headers: authHeaders,
    });
    if (res.ok) {
      toast.success('Published');
      await loadSections();
    } else toast.error('Publish failed');
  };

  const handleRevert = async () => {
    if (!selected) return;
    if (!window.confirm('Revert draft to last published version?')) return;
    const res = await fetch(`${API_URL}/api/cms/sections/${selected.id}/revert`, {
      method: 'POST', headers: authHeaders,
    });
    if (res.ok) {
      toast.success('Reverted');
      await loadSections();
      const fresh = await res.json();
      setDraftJson(JSON.stringify(fresh.content || {}, null, 2));
      setDraftContent(fresh.content || {});
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Revert failed');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete section '${selected.section_id}' from '${selected.page_slug}'? This also removes its version history.`)) return;
    const res = await fetch(`${API_URL}/api/cms/sections/${selected.id}`, {
      method: 'DELETE', headers: authHeaders,
    });
    if (res.ok) {
      toast.success('Deleted');
      setSelectedId(null);
      setDraftJson('');
      setDraftContent(null);
      await loadSections();
    } else toast.error('Delete failed');
  };

  const openVersions = async () => {
    if (!selected) return;
    const res = await fetch(`${API_URL}/api/cms/sections/${selected.id}/versions`, { headers: authHeaders });
    if (res.ok) {
      const data = await res.json();
      setVersions(data.versions || []);
      setVersionsOpen(true);
    }
  };

  // -------- Drag & drop reorder --------
  const onDragStart = (id) => setDragId(id);
  const onDrop = async (targetId) => {
    if (!dragId || dragId === targetId) return setDragId(null);
    const src = sections.find((s) => s.id === dragId);
    const tgt = sections.find((s) => s.id === targetId);
    if (!src || !tgt) return setDragId(null);
    // Simple swap of `order` values; enough for a v1 reorder UX.
    await Promise.all([
      fetch(`${API_URL}/api/cms/sections/${src.id}`, { method: 'PUT', headers: authHeaders, body: JSON.stringify({ order: tgt.order }) }),
      fetch(`${API_URL}/api/cms/sections/${tgt.id}`, { method: 'PUT', headers: authHeaders, body: JSON.stringify({ order: src.order }) }),
    ]);
    setDragId(null);
    await loadSections();
  };

  // -------- Media picker --------
  const searchMedia = useCallback(async (q) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    params.set('limit', '30');
    const res = await fetch(`${API_URL}/api/cms/media/search?${params}`, { headers: authHeaders });
    if (res.ok) {
      const data = await res.json();
      setMediaResults(data.items || []);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (mediaOpen) searchMedia(mediaQuery);
  }, [mediaOpen, mediaQuery, searchMedia]);

  const insertAssetKey = (assetKey) => {
    // Structured-editor path — invoke the pending field callback and close.
    if (mediaPickCallback) {
      mediaPickCallback(assetKey);
      setMediaPickCallback(null);
      setMediaOpen(false);
      return;
    }
    // Raw-JSON path — insert `"assetKey"` at the cursor position.
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const before = draftJson.slice(0, start);
    const after = draftJson.slice(end);
    const snippet = `"${assetKey}"`;
    setDraftJson(before + snippet + after);
    setMediaOpen(false);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + snippet.length, start + snippet.length);
    }, 0);
  };

  // Called from StructuredEditor asset_key fields.
  const openMediaFor = (cb) => {
    setMediaPickCallback(() => cb);
    setMediaOpen(true);
  };

  // -------- Create section --------
  const handleCreate = async () => {
    if (!newSlug || !newSecId) return toast.error('Page slug and section id required');
    const res = await fetch(`${API_URL}/api/cms/sections`, {
      method: 'POST', headers: authHeaders,
      body: JSON.stringify({ page_slug: newSlug, section_id: newSecId, order: (sections.length + 1) * 10, content: {} }),
    });
    if (res.ok) {
      toast.success('Section created');
      setNewSectionOpen(false);
      setNewSecId('');
      setPageSlug(newSlug);
      await loadSections();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Create failed');
    }
  };

  // -------- Preview URL --------
  const previewRoute = selected ? (PAGE_TO_ROUTE[selected.page_slug] || `/${selected.page_slug}`) : '/';
  const previewUrl = selected?.preview_token
    ? `${previewRoute}?preview=${encodeURIComponent(selected.preview_token)}`
    : previewRoute;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" data-testid="sections-manager-title">CMS Sections</h1>
          <p className="text-sm text-slate-500">Draft → Publish workflow with live preview</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={pageSlug} onValueChange={setPageSlug}>
            <SelectTrigger className="w-[180px]" data-testid="page-filter"><SelectValue placeholder="Page" /></SelectTrigger>
            <SelectContent>
              {KNOWN_PAGES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadSections} data-testid="refresh-btn"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          <Button size="sm" onClick={() => setNewSectionOpen(true)} data-testid="new-section-btn"><Plus className="w-4 h-4 mr-1" /> New</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_minmax(320px,480px)] gap-4">
        {/* List */}
        <Card className="p-3">
          <p className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2">Sections ({sections.length})</p>
          {loading ? <p className="text-sm text-slate-400">Loading…</p> : sections.length === 0 ? (
            <p className="text-sm text-slate-400">No sections yet. Click <span className="font-medium">New</span> to add one.</p>
          ) : (
            <ul className="space-y-1">
              {sections.map((s) => (
                <li key={s.id}
                    draggable
                    onDragStart={() => onDragStart(s.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(s.id)}
                    onClick={() => selectSection(s)}
                    data-testid={`section-item-${s.section_id}`}
                    className={`flex items-center gap-2 px-2 py-2 rounded cursor-pointer text-sm ${selectedId === s.id ? 'bg-ux-blue text-white' : 'hover:bg-slate-100'}`}>
                  <GripVertical className="w-3.5 h-3.5 opacity-60" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{s.section_id}</p>
                    <p className={`text-xs truncate ${selectedId === s.id ? 'text-white/80' : 'text-slate-500'}`}>{s.page_slug} · order {s.order}</p>
                  </div>
                  <Badge variant={s.status === 'published' ? 'default' : 'secondary'} className="text-[10px]">
                    {s.status === 'published' ? <CheckCircle2 className="w-3 h-3" /> : 'draft'}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Editor */}
        <Card className="p-3 space-y-3">
          {!selected ? (
            <p className="text-sm text-slate-400 p-8 text-center">Select a section to edit its content.</p>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-semibold" data-testid="editor-title">{selected.page_slug} <span className="opacity-40">/</span> {selected.section_id}</h3>
                  <p className="text-xs text-slate-500">draft v{selected.draft_version} · published v{selected.published_version} · order {selected.order}</p>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {getSectionSchema(selected.page_slug, selected.section_id) && (
                    <div className="inline-flex rounded-md border overflow-hidden mr-1" data-testid="editor-mode-toggle">
                      <button
                        type="button"
                        onClick={() => {
                          // Switch to structured — try to parse current JSON if we came from raw mode.
                          if (editorMode !== 'structured') {
                            try {
                              const parsed = JSON.parse(draftJson);
                              setDraftContent(parsed);
                              setJsonError(null);
                            } catch (e) {
                              toast.error('Fix JSON before switching to structured');
                              return;
                            }
                          }
                          setEditorMode('structured');
                        }}
                        className={`text-xs px-2 py-1 flex items-center gap-1 ${editorMode === 'structured' ? 'bg-ux-blue text-white' : 'bg-white text-slate-600'}`}
                        data-testid="editor-mode-structured"
                      >
                        <LayoutList className="w-3.5 h-3.5" /> Fields
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          // Switch to raw — serialize current structured content.
                          if (editorMode !== 'raw') {
                            setDraftJson(JSON.stringify(draftContent ?? {}, null, 2));
                          }
                          setEditorMode('raw');
                        }}
                        className={`text-xs px-2 py-1 flex items-center gap-1 ${editorMode === 'raw' ? 'bg-ux-blue text-white' : 'bg-white text-slate-600'}`}
                        data-testid="editor-mode-raw"
                      >
                        <Code2 className="w-3.5 h-3.5" /> Raw JSON
                      </button>
                    </div>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setMediaOpen(true)} data-testid="media-picker-btn">
                    <ImageIcon className="w-4 h-4 mr-1" /> Media
                  </Button>
                  <Button size="sm" variant="outline" onClick={openVersions} data-testid="versions-btn"><History className="w-4 h-4 mr-1" /> Versions</Button>
                  <Button size="sm" variant="outline" onClick={handleRevert} data-testid="revert-btn"><RotateCcw className="w-4 h-4 mr-1" /> Revert</Button>
                  <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-btn"><Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save Draft'}</Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handlePublish} data-testid="publish-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Publish
                  </Button>
                  <Button size="sm" variant="destructive" onClick={handleDelete} data-testid="delete-btn"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              {/* Live diff strip: live draft (in-memory) vs last published snapshot. */}
              {(() => {
                let liveDraft;
                if (editorMode === 'structured') {
                  liveDraft = draftContent ?? {};
                } else {
                  try { liveDraft = JSON.parse(draftJson); } catch { liveDraft = selected.content || {}; }
                }
                return (
                  <SectionDiffStrip
                    draft={liveDraft}
                    published={selected.published_content || {}}
                  />
                );
              })()}
              {(() => {
                const schema = getSectionSchema(selected.page_slug, selected.section_id);
                if (editorMode === 'structured' && schema) {
                  return (
                    <div className="max-h-[520px] overflow-y-auto pr-1" data-testid="structured-editor-wrapper">
                      <StructuredEditor
                        schema={schema}
                        value={draftContent || {}}
                        onChange={setDraftContent}
                        onPickAsset={openMediaFor}
                      />
                    </div>
                  );
                }
                return (
                  <>
                    {!schema && (
                      <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        No structured schema registered for <code>{selected.page_slug}:{selected.section_id}</code>. Using raw JSON editor.
                      </p>
                    )}
                    <Textarea
                      ref={textareaRef}
                      value={draftJson}
                      onChange={(e) => setDraftJson(e.target.value)}
                      className="font-mono text-sm min-h-[420px]"
                      data-testid="content-editor"
                      spellCheck={false}
                    />
                    {jsonError && <p className="text-xs text-red-600">JSON error: {jsonError}</p>}
                  </>
                );
              })()}
            </>
          )}
        </Card>

        {/* Preview iframe */}
        <Card className="p-2 flex flex-col">
          <div className="flex items-center justify-between p-2 border-b">
            <p className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Live preview</p>
            {selected && (
              <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs text-ux-blue hover:underline flex items-center gap-1" data-testid="open-preview-tab">
                Open in new tab <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
          {selected ? (
            <iframe
              key={selected.id + selected.draft_version}
              title="preview"
              src={previewUrl}
              className="w-full flex-1 min-h-[520px] bg-white"
              data-testid="preview-iframe"
            />
          ) : (
            <p className="text-sm text-slate-400 p-8 text-center">Preview loads when a section is selected.</p>
          )}
        </Card>
      </div>

      {/* Media picker dialog */}
      <Dialog open={mediaOpen} onOpenChange={(open) => { setMediaOpen(open); if (!open) setMediaPickCallback(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Insert media</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input
              value={mediaQuery}
              onChange={(e) => setMediaQuery(e.target.value)}
              placeholder="Search by name, tag or asset_key"
              data-testid="media-search-input"
            />
            <Button variant="outline" onClick={() => searchMedia(mediaQuery)}><Search className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-[420px] overflow-y-auto">
            {mediaResults.map((m) => (
              <button
                key={m.id}
                onClick={() => insertAssetKey(m.asset_key)}
                data-testid={`media-result-${m.asset_key}`}
                className="border rounded p-2 hover:border-ux-blue text-left"
              >
                <div className="aspect-square bg-slate-100 rounded overflow-hidden mb-1 flex items-center justify-center">
                  {m.url && (m.type === 'image' || m.type === 'hero' || m.type === 'logo' || m.type === 'icon')
                    ? <img src={m.url} alt={m.alt_text || m.name} className="w-full h-full object-cover" />
                    : <ImageIcon className="w-6 h-6 text-slate-400" />
                  }
                </div>
                <p className="text-xs font-medium truncate">{m.name}</p>
                <p className="text-[10px] text-slate-500 truncate">{m.asset_key}</p>
                {!m.alt_text && <Badge variant="destructive" className="text-[9px] mt-1">no alt</Badge>}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Versions dialog */}
      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Version history</DialogTitle></DialogHeader>
          {versions.length === 0 ? <p className="text-sm text-slate-500">No published versions yet.</p> : (
            <ul className="space-y-2 max-h-[420px] overflow-y-auto">
              {versions.map((v) => (
                <li key={`${v.version}-${v.published_at}`} className="border rounded p-3 text-sm">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-semibold">v{v.version}</span>
                    <span className="text-xs text-slate-500">{new Date(v.published_at).toLocaleString()}</span>
                  </div>
                  <pre className="text-xs bg-slate-50 rounded p-2 overflow-x-auto max-h-[160px]">{JSON.stringify(v.content, null, 2)}</pre>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {/* New section dialog */}
      <Dialog open={newSectionOpen} onOpenChange={setNewSectionOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New section</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Page slug</label>
              <Select value={newSlug} onValueChange={setNewSlug}>
                <SelectTrigger data-testid="new-page-slug"><SelectValue /></SelectTrigger>
                <SelectContent>{KNOWN_PAGES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold">Section id</label>
              <Input value={newSecId} onChange={(e) => setNewSecId(e.target.value)} placeholder="e.g. hero, final_cta" data-testid="new-section-id" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewSectionOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="new-section-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SectionsManager;
